import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './Dialog';
import { FileCode, GitCompare } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './Tabs';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import styles from './FileDiffViewer.module.css';

interface FileDiffViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentFiles: Selectable<SessionFiles>[];
  checkpointFiles: Array<{
    fileName: string;
    filePath: string;
    content: string;
    language?: string;
  }>;
  checkpointName?: string;
  /** If set, auto-select this file on open */
  initialFile?: string;
}

type DiffLine = {
  oldLine: number | null;
  newLine: number | null;
  type: 'added' | 'removed' | 'unchanged';
  content: string;
};

/**
 * Simple LCS-based diff: produces a unified diff with old/new line numbers.
 */
function computeDiff(oldContent: string, newContent: string): DiffLine[] {
  const oldLines = oldContent.split('\n');
  const newLines = newContent.split('\n');

  // Build LCS table (Myers-like, but simple DP for correctness)
  const m = oldLines.length;
  const n = newLines.length;

  // For very large files, fall back to simple line-by-line comparison
  if (m * n > 500_000) {
    return simpleDiff(oldLines, newLines);
  }

  // DP LCS
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to produce diff
  const result: DiffLine[] = [];
  let i = m;
  let j = n;
  const stack: DiffLine[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      stack.push({ oldLine: i, newLine: j, type: 'unchanged', content: oldLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ oldLine: null, newLine: j, type: 'added', content: newLines[j - 1] });
      j--;
    } else {
      stack.push({ oldLine: i, newLine: null, type: 'removed', content: oldLines[i - 1] });
      i--;
    }
  }

  // Reverse (we built it backwards)
  for (let k = stack.length - 1; k >= 0; k--) {
    result.push(stack[k]);
  }

  return result;
}

/** Fallback for very large files — simple sequential comparison */
function simpleDiff(oldLines: string[], newLines: string[]): DiffLine[] {
  const result: DiffLine[] = [];
  let oi = 0;
  let ni = 0;
  let oldNum = 1;
  let newNum = 1;

  while (oi < oldLines.length || ni < newLines.length) {
    if (oi >= oldLines.length) {
      result.push({ oldLine: null, newLine: newNum++, type: 'added', content: newLines[ni++] });
    } else if (ni >= newLines.length) {
      result.push({ oldLine: oldNum++, newLine: null, type: 'removed', content: oldLines[oi++] });
    } else if (oldLines[oi] === newLines[ni]) {
      result.push({
        oldLine: oldNum++,
        newLine: newNum++,
        type: 'unchanged',
        content: oldLines[oi],
      });
      oi++;
      ni++;
    } else {
      // Look ahead for a match
      let found = false;
      for (let look = 1; look < Math.min(10, newLines.length - ni); look++) {
        if (oldLines[oi] === newLines[ni + look]) {
          for (let a = 0; a < look; a++) {
            result.push({
              oldLine: null,
              newLine: newNum++,
              type: 'added',
              content: newLines[ni++],
            });
          }
          result.push({
            oldLine: oldNum++,
            newLine: newNum++,
            type: 'unchanged',
            content: oldLines[oi],
          });
          oi++;
          ni++;
          found = true;
          break;
        }
      }
      if (!found) {
        result.push({ oldLine: oldNum++, newLine: null, type: 'removed', content: oldLines[oi++] });
        result.push({ oldLine: null, newLine: newNum++, type: 'added', content: newLines[ni++] });
      }
    }
  }
  return result;
}

/** Collapse unchanged lines that are far from changes, showing context of N lines */
function collapseUnchanged(
  diff: DiffLine[],
  context: number = 3,
): (DiffLine | { type: 'separator'; count: number })[] {
  // Mark which lines are within `context` lines of a change
  const show = new Set<number>();
  for (let i = 0; i < diff.length; i++) {
    if (diff[i].type !== 'unchanged') {
      for (let j = Math.max(0, i - context); j <= Math.min(diff.length - 1, i + context); j++) {
        show.add(j);
      }
    }
  }

  const result: (DiffLine | { type: 'separator'; count: number })[] = [];
  let hiddenCount = 0;

  for (let i = 0; i < diff.length; i++) {
    if (show.has(i)) {
      if (hiddenCount > 0) {
        result.push({ type: 'separator', count: hiddenCount });
        hiddenCount = 0;
      }
      result.push(diff[i]);
    } else {
      hiddenCount++;
    }
  }
  if (hiddenCount > 0) {
    result.push({ type: 'separator', count: hiddenCount });
  }

  return result;
}

export const FileDiffViewer: React.FC<FileDiffViewerProps> = ({
  open,
  onOpenChange,
  currentFiles,
  checkpointFiles,
  checkpointName = 'Checkpoint',
  initialFile,
}) => {
  const [selectedFile, setSelectedFile] = React.useState<string | null>(null);

  // Create a map of checkpoint files
  const checkpointFileMap = useMemo(() => {
    const map = new Map<string, (typeof checkpointFiles)[0]>();
    checkpointFiles.forEach((f) => {
      const key = `${f.filePath || '/'}${f.fileName}`;
      map.set(key, f);
    });
    return map;
  }, [checkpointFiles]);

  // Get files that have differences
  const changedFiles = useMemo(() => {
    const changed: Array<{
      key: string;
      fileName: string;
      filePath: string;
      currentContent: string;
      checkpointContent: string;
      language?: string;
      status: 'modified' | 'added' | 'deleted';
    }> = [];

    currentFiles.forEach((file) => {
      const key = `${file.filePath || '/'}${file.fileName}`;
      const checkpointFile = checkpointFileMap.get(key);

      if (!checkpointFile) {
        changed.push({
          key,
          fileName: file.fileName,
          filePath: file.filePath || '/',
          currentContent: file.content || '',
          checkpointContent: '',
          language: file.language || undefined,
          status: 'added',
        });
      } else if (checkpointFile.content !== file.content) {
        changed.push({
          key,
          fileName: file.fileName,
          filePath: file.filePath || '/',
          currentContent: file.content || '',
          checkpointContent: checkpointFile.content || '',
          language: file.language || checkpointFile.language || undefined,
          status: 'modified',
        });
      }
    });

    checkpointFiles.forEach((checkpointFile) => {
      const key = `${checkpointFile.filePath || '/'}${checkpointFile.fileName}`;
      const currentFile = currentFiles.find((f) => `${f.filePath || '/'}${f.fileName}` === key);
      if (!currentFile) {
        changed.push({
          key,
          fileName: checkpointFile.fileName,
          filePath: checkpointFile.filePath || '/',
          currentContent: '',
          checkpointContent: checkpointFile.content || '',
          language: checkpointFile.language || undefined,
          status: 'deleted',
        });
      }
    });

    return changed;
  }, [currentFiles, checkpointFileMap, checkpointFiles]);

  // Auto-select file
  React.useEffect(() => {
    if (!open) return;
    if (initialFile) {
      const match = changedFiles.find(
        (f) => f.fileName === initialFile || f.key.endsWith(initialFile),
      );
      if (match) {
        setSelectedFile(match.key);
        return;
      }
    }
    if (changedFiles.length > 0 && !selectedFile) {
      setSelectedFile(changedFiles[0].key);
    }
  }, [changedFiles, selectedFile, open, initialFile]);

  // Reset selection when dialog closes
  React.useEffect(() => {
    if (!open) setSelectedFile(null);
  }, [open]);

  const selectedFileData = changedFiles.find((f) => f.key === selectedFile);

  // Compute diff for the selected file
  const diffResult = useMemo(() => {
    if (!selectedFileData) return { lines: [], added: 0, removed: 0 };
    const rawDiff = computeDiff(
      selectedFileData.checkpointContent,
      selectedFileData.currentContent,
    );
    const added = rawDiff.filter((l) => l.type === 'added').length;
    const removed = rawDiff.filter((l) => l.type === 'removed').length;
    const lines = collapseUnchanged(rawDiff);
    return { lines, added, removed };
  }, [selectedFileData]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            <GitCompare size={20} className={styles.icon} />
            Changes vs {checkpointName}
          </DialogTitle>
          <DialogDescription>
            {changedFiles.length} file{changedFiles.length !== 1 ? 's' : ''} changed
          </DialogDescription>
        </DialogHeader>

        {changedFiles.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No changes detected</p>
          </div>
        ) : (
          <div className={styles.contentArea}>
            <Tabs
              value={selectedFile || undefined}
              onValueChange={setSelectedFile}
              className={styles.tabs}
            >
              <TabsList className={styles.tabsList}>
                {changedFiles.map((file) => (
                  <TabsTrigger key={file.key} value={file.key} className={styles.tabTrigger}>
                    <FileCode size={14} className={styles.fileIcon} />
                    <span className={styles.fileName}>{file.fileName}</span>
                    <span className={`${styles.statusBadge} ${styles[file.status]}`}>
                      {file.status === 'added' ? '+' : file.status === 'modified' ? 'M' : 'D'}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {changedFiles.map((file) => (
                <TabsContent key={file.key} value={file.key} className={styles.tabContent}>
                  <div className={styles.diffContainer}>
                    <div className={styles.diffHeader}>
                      <div className={styles.diffHeaderLeft}>
                        <span className={styles.filePath}>
                          {file.filePath}
                          {file.fileName}
                        </span>
                      </div>
                      {selectedFile === file.key && (
                        <div className={styles.diffStats}>
                          <span className={styles.diffStatsAdded}>+{diffResult.added}</span>
                          <span className={styles.diffStatsRemoved}>-{diffResult.removed}</span>
                        </div>
                      )}
                    </div>
                    {selectedFile === file.key && (
                      <div className={styles.diffView}>
                        {diffResult.lines.map((entry, idx) => {
                          if ('count' in entry && entry.type === 'separator') {
                            return (
                              <div key={`sep-${idx}`} className={styles.diffHunkSep}>
                                ... {entry.count} unchanged line{entry.count !== 1 ? 's' : ''} ...
                              </div>
                            );
                          }
                          const line = entry as DiffLine;
                          const lineClass =
                            line.type === 'added'
                              ? styles.diffLineAdded
                              : line.type === 'removed'
                                ? styles.diffLineRemoved
                                : styles.diffLineUnchanged;
                          const prefix =
                            line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' ';
                          return (
                            <div key={idx} className={`${styles.diffLine} ${lineClass}`}>
                              <span className={styles.diffLineNumber}>{line.oldLine ?? ''}</span>
                              <span className={styles.diffLineNumber}>{line.newLine ?? ''}</span>
                              <span className={styles.diffLinePrefix}>{prefix}</span>
                              <span className={styles.diffLineContent}>{line.content}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
